import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import ActualRewards from '@sorare/core/src/components/lineup/ActualRewards';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import UserName from '@sorare/core/src/components/user/UserName';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import {
  ClickMarketTrendsLink_ForYouHeuristic,
  ClickMarketTrendsLink_TileType,
} from '@sorare/core/src/protos/events/platform/web/events';

import { PlayerRecommendationRow } from 'components/forYou/PlayerRecommendationRow';
import { GameWeekChip } from 'components/forYou/chips/GameWeekChip';
import FlexCard from 'components/token/FlexCard';

import { WinningTeams_winningLineup } from './__generated__/index.graphql';

const Extra = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  flex-grow: 1;
  align-items: flex-start;
  justify-content: flex-end;
  padding-top: var(--quadruple-unit);
`;

const ActualRewardsWrapper = styled.div`
  background-color: rgba(var(--c-rgb-neutral-300), 0.75);
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
`;

const CardWrapper = styled(Vertical)`
  width: 20%;
`;

const TokenWrapper = styled.div<{ sport: Sport }>`
  ${({ sport }) => {
    switch (sport) {
      case Sport.BASEBALL:
        return css`
          margin-inline: auto;
          width: calc(7 * var(--unit));
        `;
      case Sport.FOOTBALL:
      default:
        return css``;
    }
  }}
`;

const Score = styled(LabelS)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: var(--quadruple-unit);
  width: var(--quadruple-unit);
  border-radius: 50%;
  border: 1px solid var(--c-neutral-1000);
`;

const Cards = styled(Horizontal)<{ sport: Sport }>`
  ${({ sport }) => {
    switch (sport) {
      case Sport.BASEBALL:
        return css`
          margin-inline: auto;
          flex-direction: row-reverse;
          flex-wrap: wrap;
          justify-content: center;
        `;
      case Sport.FOOTBALL:
      default:
        return css``;
    }
  }}
`;

type Props = {
  winningLineup: Nullable<WinningTeams_winningLineup>;
  loading: boolean;
  onStatsClick: (slug: string) => void;
  so5LeagueSlug: string;
  sport: Sport;
};

export const WinningTeams = ({
  winningLineup,
  loading,
  onStatsClick,
  so5LeagueSlug,
  sport,
}: Props) => {
  const track = useEvents();
  const { isIosApp } = useIsMobileApp();
  const eventProperties = {
    so5LeagueSlug,
    heuristic: ClickMarketTrendsLink_ForYouHeuristic.WINNING_LINEUP,
  };
  const trackForYouClick = (e: {
    userSlug?: string;
    scarcity?: Rarity;
    playerSlug?: string;
    serialNumber?: number;
  }) =>
    track('Click Market Trends Link', {
      tileType: ClickMarketTrendsLink_TileType.TITLE,
      ...eventProperties,
      ...e,
    });

  const players =
    winningLineup?.so5Lineup?.so5Appearances?.map(
      ({ anyPlayer }) => anyPlayer
    ) || [];
  const so5Appearances = winningLineup?.so5Lineup?.so5Appearances || [];

  const user = winningLineup?.so5Lineup?.user;
  const gameWeek = winningLineup?.so5Leaderboard?.so5Fixture?.shortDisplayName;

  const so5Rewards = winningLineup?.so5Lineup?.so5Rankings?.[0]?.so5Rewards;

  const winner = user && (
    <Horizontal center>
      <Avatar user={user} variant="small" />
      <UserName user={user} />
    </Horizontal>
  );

  return (
    <PlayerRecommendationRow
      title={
        <FormattedMessage
          defaultMessage="Winning{br}Lineup{br}{gameWeek}"
          id="market.trending.forYou.winningTeams.title"
          values={{
            br: <br />,
            gameWeek,
          }}
        />
      }
      background="linear-gradient(92deg, #B08037 2.27%, #B7271A 96.89%)"
      extra={
        <Extra gap={8}>
          {user && (
            <ConditionalWrapper
              wrap={!isIosApp}
              Wrapper={MyClubLink}
              props={{
                user,
                onClick: () =>
                  trackForYouClick({
                    userSlug: user.slug,
                  }),
              }}
            >
              {winner}
            </ConditionalWrapper>
          )}
          {so5Rewards && (
            <ActualRewardsWrapper>
              <ActualRewards rewards={so5Rewards} wrap withoutLink={isIosApp} />
            </ActualRewardsWrapper>
          )}
          <Cards sport={sport}>
            {so5Appearances.map(
              ({ score, anyCard, anyPlayer }) =>
                anyCard && (
                  <CardWrapper key={anyCard.slug}>
                    <TokenWrapper sport={sport}>
                      <FlexCard
                        card={anyCard}
                        withLink={!isIosApp}
                        onClick={() =>
                          trackForYouClick({
                            playerSlug: anyPlayer.slug,
                            serialNumber: anyCard.serialNumber,
                            scarcity: anyCard.rarityTyped,
                          })
                        }
                      />
                    </TokenWrapper>
                    <Horizontal center>
                      <Score bold as="span" color="var(--c-neutral-1000)">
                        {Math.round(score)}
                      </Score>
                    </Horizontal>
                  </CardWrapper>
                )
            )}
          </Cards>
        </Extra>
      }
      chip={<GameWeekChip gameWeek={gameWeek} />}
      loading={loading}
      players={players}
      onStatsClick={onStatsClick}
      eventProperties={eventProperties}
    />
  );
};
WinningTeams.displayName = 'WinningTeams';

WinningTeams.fragments = {
  winningLineup: gql`
    fragment WinningTeams_winningLineup on WinningLineup {
      so5Leaderboard {
        slug
        gameWeek
        so5Fixture {
          slug
          shortDisplayName
        }
      }
      so5Lineup {
        id
        user {
          slug
          ...MyClubLink_publicUserInfoInterface
          ...Avatar_publicUserInfoInterface
          ...UserName_publicUserInfoInterface
        }
        so5Rankings {
          id
          so5Rewards {
            slug
            ...ActualRewards_so5Reward
          }
        }
        so5Appearances {
          id
          score(withBonus: false)
          anyCard {
            slug
            serialNumber
            rarityTyped
            ...FlexCard_anyCard
          }
          anyPlayer {
            slug
            ...PlayerRecommendationRow_anyPlayer
          }
        }
      }
    }
    ${PlayerRecommendationRow.fragments.anyPlayer}
    ${FlexCard.fragments.anyCard}
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
    ${MyClubLink.fragments.user}
    ${ActualRewards.fragments.so5Reward}
  ` as TypedDocumentNode<WinningTeams_winningLineup>,
};
